import {
  APPEARANCE_OPTIONS,
  DEFAULT_PLAYER_APPEARANCE,
  generateCharacterAppearance,
  type CharacterAppearance,
} from "../sim/appearance.ts";

export interface AppearanceCustomizerCallbacks {
  onOpenChange(open: boolean): void;
  onChange(appearance: Readonly<CharacterAppearance>): void;
  onViewChange(view: AppearanceInspectionView): void;
}

export type AppearanceInspectionView = "front" | "back" | "face";

export interface AppearanceCustomizer {
  setAppearance(appearance: Readonly<CharacterAppearance>): void;
  toggle(): void;
  close(): void;
  isOpen(): boolean;
  dispose(): void;
}

type AppearanceKey = Exclude<
  keyof CharacterAppearance,
  "schemaVersion" | "colours"
>;
type ColourKey = keyof CharacterAppearance["colours"];

const PALETTE_PRESETS = Object.freeze([
  {
    id: "old-brick",
    label: "Old brick",
    colours: { outerwear: 0x795243, lower: 0x383d3b, accent: 0xb98b5e },
  },
  {
    id: "river-mist",
    label: "River mist",
    colours: { outerwear: 0x596b6d, lower: 0x39444d, accent: 0x9eb0aa },
  },
  {
    id: "pine-shade",
    label: "Pine shade",
    colours: { outerwear: 0x4f6354, lower: 0x353d38, accent: 0xa59667 },
  },
  {
    id: "plum-wool",
    label: "Plum wool",
    colours: { outerwear: 0x69505f, lower: 0x3e3a43, accent: 0xb38a82 },
  },
] as const);

function element<K extends keyof HTMLElementTagNameMap>(
  tagName: K,
  className: string,
): HTMLElementTagNameMap[K] {
  const created = document.createElement(tagName);
  created.className = className;
  return created;
}

function copyAppearance(
  appearance: Readonly<CharacterAppearance>,
): CharacterAppearance {
  return {
    ...appearance,
    colours: { ...appearance.colours },
  };
}

function hexColour(value: number): string {
  return "#" + value.toString(16).padStart(6, "0");
}

function colourNumber(value: string): number {
  return Number.parseInt(value.slice(1), 16);
}

export function createAppearanceCustomizer(
  root: HTMLElement,
  initialAppearance: Readonly<CharacterAppearance>,
  callbacks: Readonly<AppearanceCustomizerCallbacks>,
): AppearanceCustomizer {
  let appearance = copyAppearance(initialAppearance);
  let open = false;
  let inspectionView: AppearanceInspectionView = "front";
  let randomRevision = 0;

  const toggleButton = element("button", "appearance-toggle");
  toggleButton.type = "button";
  toggleButton.setAttribute("aria-expanded", "false");
  toggleButton.setAttribute("aria-controls", "appearance-panel");
  const toggleMark = element("span", "appearance-toggle-mark");
  toggleMark.setAttribute("aria-hidden", "true");
  const toggleCopy = element("span", "appearance-toggle-copy");
  const toggleKicker = element("span", "appearance-toggle-kicker");
  toggleKicker.textContent = "Wardrobe";
  const toggleDetail = element("span", "appearance-toggle-detail");
  toggleDetail.textContent = "Your look";
  toggleCopy.append(toggleKicker, toggleDetail);
  const toggleKey = element("kbd", "appearance-toggle-key");
  toggleKey.textContent = "U";
  toggleButton.append(toggleMark, toggleCopy, toggleKey);

  const panel = element("section", "appearance-panel");
  panel.id = "appearance-panel";
  panel.hidden = true;
  panel.setAttribute("role", "dialog");
  panel.setAttribute("aria-modal", "false");
  panel.setAttribute("aria-labelledby", "appearance-title");

  const header = element("header", "appearance-header");
  const headingGroup = element("div", "appearance-heading-group");
  const eyebrow = element("p", "appearance-eyebrow");
  eyebrow.textContent = "A mirror in the hall";
  const title = element("h2", "appearance-title");
  title.id = "appearance-title";
  title.textContent = "Your look";
  const intro = element("p", "appearance-intro");
  intro.textContent =
    "Shape a recognisable silhouette. Changes appear on your character immediately.";
  const inspection = element("div", "appearance-inspection");
  const inspectionLabel = element("span", "appearance-inspection-label");
  inspectionLabel.textContent = "Mirror view";
  const viewButtons = new Map<AppearanceInspectionView, HTMLButtonElement>();
  for (const [view, label] of [
    ["front", "Front"],
    ["back", "Back"],
    ["face", "Face"],
  ] as const) {
    const button = element("button", "appearance-view-button");
    button.type = "button";
    button.dataset.view = view;
    button.textContent = label;
    button.setAttribute("aria-pressed", "false");
    viewButtons.set(view, button);
    inspection.append(button);
  }
  inspection.prepend(inspectionLabel);
  headingGroup.append(eyebrow, title, intro, inspection);
  const closeButton = element("button", "appearance-close");
  closeButton.type = "button";
  closeButton.setAttribute("aria-label", "Close appearance customizer");
  closeButton.textContent = "Done";
  header.append(headingGroup, closeButton);

  const selectByKey = new Map<AppearanceKey, HTMLSelectElement>();
  const colourByKey = new Map<ColourKey, HTMLInputElement>();

  function selectControl(
    key: AppearanceKey,
    labelText: string,
    options: readonly Readonly<{ id: string; label: string }>[],
  ): HTMLLabelElement {
    const label = element("label", "appearance-field");
    const caption = element("span", "appearance-field-label");
    caption.textContent = labelText;
    const select = element("select", "appearance-select");
    select.dataset.appearanceKey = key;
    for (const option of options) {
      const choice = document.createElement("option");
      choice.value = option.id;
      choice.textContent = option.label;
      select.append(choice);
    }
    label.append(caption, select);
    selectByKey.set(key, select);
    return label;
  }

  function colourControl(
    key: ColourKey,
    labelText: string,
  ): HTMLLabelElement {
    const label = element("label", "appearance-colour");
    const input = element("input", "appearance-colour-input");
    input.type = "color";
    input.dataset.appearanceColour = key;
    const copy = element("span", "appearance-colour-copy");
    const caption = element("span", "appearance-field-label");
    caption.textContent = labelText;
    const value = element("output", "appearance-colour-value");
    copy.append(caption, value);
    label.append(input, copy);
    colourByKey.set(key, input);
    return label;
  }

  function section(labelText: string): {
    readonly section: HTMLElement;
    readonly fields: HTMLElement;
  } {
    const sectionElement = element("section", "appearance-section");
    const label = element("h3", "appearance-section-title");
    label.textContent = labelText;
    const fields = element("div", "appearance-fields");
    sectionElement.append(label, fields);
    return { section: sectionElement, fields };
  }

  const build = section("Silhouette");
  build.fields.append(
    selectControl("faceShape", "Face", APPEARANCE_OPTIONS.faceShapes),
    selectControl("bodyShape", "Build", APPEARANCE_OPTIONS.bodyShapes),
    selectControl("stature", "Stature", APPEARANCE_OPTIONS.statures),
    selectControl("ageGroup", "Age", APPEARANCE_OPTIONS.ageGroups),
  );

  const features = section("Face & hair");
  features.fields.append(
    selectControl("skinTone", "Skin tone", APPEARANCE_OPTIONS.skinTones),
    selectControl("hairStyle", "Hair", APPEARANCE_OPTIONS.hairStyles),
    selectControl(
      "hairColour",
      "Hair colour",
      APPEARANCE_OPTIONS.hairColours,
    ),
    selectControl(
      "facialHair",
      "Facial hair",
      APPEARANCE_OPTIONS.facialHair,
    ),
    selectControl("eyeColour", "Eyes", APPEARANCE_OPTIONS.eyeColours),
  );

  const clothing = section("Clothing");
  clothing.fields.append(
    selectControl("outerwear", "Outer layer", APPEARANCE_OPTIONS.outerwear),
    selectControl(
      "lowerGarment",
      "Lower layer",
      APPEARANCE_OPTIONS.lowerGarments,
    ),
    selectControl("footwear", "Footwear", APPEARANCE_OPTIONS.footwear),
  );
  const palette = element("div", "appearance-palette");
  palette.append(
    colourControl("outerwear", "Outer"),
    colourControl("lower", "Lower"),
    colourControl("accent", "Accent"),
  );
  clothing.section.append(palette);
  const palettePresets = element("div", "appearance-palette-presets");
  const palettePresetLabel = element("span", "appearance-preset-label");
  palettePresetLabel.textContent = "Vilnius cloth palettes";
  palettePresets.append(palettePresetLabel);
  const palettePresetButtons: HTMLButtonElement[] = [];
  for (const preset of PALETTE_PRESETS) {
    const button = element("button", "appearance-palette-preset");
    button.type = "button";
    button.dataset.paletteId = preset.id;
    button.title = preset.label;
    button.setAttribute("aria-label", "Use " + preset.label + " palette");
    button.style.setProperty(
      "--palette-outer",
      hexColour(preset.colours.outerwear),
    );
    button.style.setProperty(
      "--palette-lower",
      hexColour(preset.colours.lower),
    );
    button.style.setProperty(
      "--palette-accent",
      hexColour(preset.colours.accent),
    );
    const name = element("span", "appearance-palette-preset-name");
    name.textContent = preset.label;
    button.append(name);
    button.addEventListener("click", () =>
      commit({
        ...appearance,
        colours: { ...preset.colours },
      })
    );
    palettePresetButtons.push(button);
    palettePresets.append(button);
  }
  clothing.section.append(palettePresets);

  const details = section("Details");
  details.fields.append(
    selectControl("eyewear", "Glasses", APPEARANCE_OPTIONS.eyewear),
    selectControl("headwear", "Headwear", APPEARANCE_OPTIONS.headwear),
    selectControl("neckwear", "Neckwear", APPEARANCE_OPTIONS.neckwear),
    selectControl(
      "carriedAccessory",
      "Carried item",
      APPEARANCE_OPTIONS.carriedAccessories,
    ),
  );

  const footer = element("footer", "appearance-footer");
  const randomizeButton = element("button", "appearance-randomize");
  randomizeButton.type = "button";
  randomizeButton.textContent = "Try another look";
  const resetButton = element("button", "appearance-reset");
  resetButton.type = "button";
  resetButton.textContent = "Restore original";
  const persistence = element("p", "appearance-persistence");
  persistence.textContent = "Saved in this browser";
  footer.append(randomizeButton, resetButton, persistence);

  panel.append(
    header,
    build.section,
    features.section,
    clothing.section,
    details.section,
    footer,
  );
  root.append(toggleButton, panel);

  function render(): void {
    for (const [key, select] of selectByKey) {
      select.value = appearance[key];
    }
    for (const [key, input] of colourByKey) {
      const value = hexColour(appearance.colours[key]);
      input.value = value;
      const output = input.parentElement?.querySelector("output");
      if (output instanceof HTMLOutputElement) {
        output.value = value.toUpperCase();
      }
    }
    toggleMark.style.setProperty(
      "--appearance-swatch",
      hexColour(appearance.colours.outerwear),
    );
    for (const [view, button] of viewButtons) {
      button.setAttribute("aria-pressed", String(view === inspectionView));
    }
    for (const button of palettePresetButtons) {
      const preset = PALETTE_PRESETS.find(
        ({ id }) => id === button.dataset.paletteId,
      );
      button.dataset.selected = String(
        preset !== undefined &&
          preset.colours.outerwear === appearance.colours.outerwear &&
          preset.colours.lower === appearance.colours.lower &&
          preset.colours.accent === appearance.colours.accent,
      );
    }
  }

  function commit(nextAppearance: Readonly<CharacterAppearance>): void {
    appearance = copyAppearance(nextAppearance);
    render();
    callbacks.onChange(appearance);
  }

  function setOpen(nextOpen: boolean): void {
    if (open === nextOpen) {
      return;
    }
    open = nextOpen;
    panel.hidden = !open;
    toggleButton.hidden = open;
    toggleButton.dataset.open = String(open);
    toggleButton.setAttribute("aria-expanded", String(open));
    callbacks.onOpenChange(open);
    if (open) {
      inspectionView = "front";
      render();
      callbacks.onViewChange(inspectionView);
      closeButton.focus({ preventScroll: true });
    }
  }

  function onSelectChange(event: Event): void {
    const select = event.currentTarget as HTMLSelectElement;
    const key = select.dataset.appearanceKey as AppearanceKey;
    commit({
      ...appearance,
      [key]: select.value,
    } as CharacterAppearance);
  }

  function onColourInput(event: Event): void {
    const input = event.currentTarget as HTMLInputElement;
    const key = input.dataset.appearanceColour as ColourKey;
    commit({
      ...appearance,
      colours: {
        ...appearance.colours,
        [key]: colourNumber(input.value),
      },
    });
  }

  function onGlobalKeyDown(event: KeyboardEvent): void {
    const target = event.target;
    const editing =
      target instanceof HTMLInputElement ||
      target instanceof HTMLSelectElement ||
      target instanceof HTMLTextAreaElement;
    if (event.code === "KeyU" && !editing && !event.repeat) {
      event.preventDefault();
      event.stopImmediatePropagation();
      setOpen(!open);
    } else if (event.code === "Escape" && open) {
      event.preventDefault();
      event.stopImmediatePropagation();
      setOpen(false);
    }
  }

  toggleButton.addEventListener("click", () => setOpen(!open));
  closeButton.addEventListener("click", () => setOpen(false));
  for (const [view, button] of viewButtons) {
    button.addEventListener("click", () => {
      inspectionView = view;
      render();
      callbacks.onViewChange(view);
    });
  }
  for (const select of selectByKey.values()) {
    select.addEventListener("change", onSelectChange);
  }
  for (const input of colourByKey.values()) {
    input.addEventListener("input", onColourInput);
  }
  randomizeButton.addEventListener("click", () => {
    randomRevision += 1;
    commit(generateCharacterAppearance(
      "local-player:wardrobe:" + Date.now() + ":" + randomRevision,
    ));
  });
  resetButton.addEventListener("click", () =>
    commit(DEFAULT_PLAYER_APPEARANCE)
  );
  window.addEventListener("keydown", onGlobalKeyDown, { capture: true });
  render();

  return {
    setAppearance(nextAppearance): void {
      appearance = copyAppearance(nextAppearance);
      render();
    },
    toggle(): void {
      setOpen(!open);
    },
    close(): void {
      setOpen(false);
    },
    isOpen(): boolean {
      return open;
    },
    dispose(): void {
      if (open) {
        callbacks.onOpenChange(false);
      }
      window.removeEventListener("keydown", onGlobalKeyDown, {
        capture: true,
      });
      toggleButton.remove();
      panel.remove();
    },
  };
}
